import { useMemo, useState } from "react";
import { Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  cadenceMs,
  freshness,
  fromCanonicalMgdl,
  glucoseNumberFormat,
  toCanonicalMgdl,
  type GlucoseSample,
  type GlucoseUnit,
  parseLocaleNumber,
} from "@heraion/shared";

import { Button } from "../primitives/Button";
import { StaleBadge } from "../primitives/StaleBadge";
import { TextField } from "../primitives/TextField";
import { makeQuickEntryCardStyles } from "./glanceCardStyles";
import type { UiTheme } from "../theme";

// Quick glucose entry, mirroring QuickWeightCard. Unlike weight (Home-only by
// policy), Antenore wants glucose entry in BOTH Home and Settings -> Measurements,
// so this shared card is mounted in both. The write is a host seam (onSave):
// mobile goes through useGlucose (POST + offline outbox). Storage is canonical
// mg/dL; the card shows + accepts the user's chosen unit (mmol/L default).
//
// HM-1 (2026-07-15): this took `latestMgDl: number | null` — a bare number, so
// THE PRIMITIVE COULD NOT EXPRESS "OLD" even if the host knew. It rendered
// «Latest: 118» with no date, forever, for the most recent reading that ever
// existed. It now takes the recent readings: [0] is the value, and the tail is
// what lets it learn whether this feed breathes every 5 minutes or every 5 days
// (thresholds are derived, never declared — the card cannot know if a CGM or a
// thumb is behind it, and the answer changes week to week).
//
// The value is NEVER hidden when stale: it is still the best datum we have, and
// hiding it would be a second lie. It just stops being announced as current —
// the badge dates it, and the badge appears ONLY on a silence longer than this
// feed's own habit, so a live CGM never shows one.
const numOrUndef = (s: string): number | undefined => {
  const v = parseLocaleNumber(s);
  return Number.isFinite(v) && v > 0 ? v : undefined;
};

const unitLabel = (u: GlucoseUnit): string => (u === "mgdl" ? "mg/dL" : "mmol/L");

export function QuickGlucoseCard({
  theme,
  unit,
  readings,
  nowMs,
  onSave,
  value: controlledValue,
  onChangeText: controlledOnChangeText,
}: {
  theme: UiTheme;
  unit: GlucoseUnit;
  /** Recent canonical (mg/dL) readings, NEWEST-FIRST. [0] is the current value;
   *  the tail is read only to learn the feed's cadence. Empty when none yet. */
  readings: readonly GlucoseSample[];
  /** Clock, injected so the age is testable and stable across a render. */
  nowMs: number;
  /** Persist a canonical mg/dL reading; returns true on success. */
  onSave: (canonicalMgDl: number) => Promise<boolean>;
  /** Optional controlled draft (MX-8b: same lift as QuickWeightCard's, see
   *  its header note). Omit to keep this card's own uncontrolled draft,
   *  unchanged (this card is mounted in both Home and Settings ->
   *  Measurements, and only the Home mount lifts its draft). */
  value?: string;
  onChangeText?: (v: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const styles = makeQuickEntryCardStyles(theme);

  const latest = readings[0];
  const current =
    latest !== undefined ? fromCanonicalMgdl(latest.mgDl, unit) : null;

  // freshness() reads ascending instants; the feed hands them newest-first.
  const age = useMemo(() => {
    const xs = [...readings].reverse().map((r) => Date.parse(r.takenAt));
    return { fresh: freshness(xs, nowMs), cadence: cadenceMs(xs) };
  }, [readings, nowMs]);
  const stale = age.fresh.known && age.fresh.stale;

  const [draft, setDraft] = useState("");
  const value = controlledValue ?? draft;
  const setValue = controlledOnChangeText ?? setDraft;
  const [saving, setSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const submit = async () => {
    const parsed = numOrUndef(value);
    if (parsed === undefined || saving) return;
    setSaving(true);
    setJustSaved(false);
    try {
      const ok = await onSave(toCanonicalMgdl(parsed, unit));
      if (ok) {
        setValue("");
        setJustSaved(true);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.card}>
      <View style={styles.textCol}>
        <Text style={styles.label}>
          {t("mobile.metabolic.glucoseTitle", { defaultValue: "Glucose" })}
        </Text>
        <Text style={[styles.hint, current !== null && !justSaved && styles.hintValue]}>
          {justSaved
            ? t("mobile.home.weightSaved", { defaultValue: "Saved ✓" })
            : current !== null
              ? t("mobile.metabolic.glucoseCurrent", {
                  defaultValue: "Latest: {{value}} {{unit}}",
                  // PG-56: 0 decimals for mg/dL, 1 for mmol/L, the shared
                  // precision rule, not a bare NumberFormat (that rendered a
                  // mmol/L reading with no decimal, e.g. "6" instead of "6.1").
                  value: glucoseNumberFormat(unit, i18n.language).format(current),
                  unit: unitLabel(unit),
                })
              : t("mobile.metabolic.glucoseNone", {
                  defaultValue: "Log a reading to track your glucose.",
                })}
        </Text>
        {stale && latest && !justSaved ? (
          <StaleBadge
            theme={theme}
            atMs={Date.parse(latest.takenAt)}
            cadenceMs={age.cadence}
          />
        ) : null}
      </View>
      <TextField
        theme={theme}
        numeric
        style={styles.input}
        value={value}
        onChangeText={setValue}
        placeholder={current !== null ? String(current) : unitLabel(unit)}
        accessibilityLabel={t("mobile.metabolic.glucoseTitle", {
          defaultValue: "Glucose",
        })}
        returnKeyType="done"
        onSubmitEditing={() => void submit()}
      />
      <Button
        theme={theme}
        // ⚠️ Was borrowing the WEIGHT card's key, so editing one string moved
        // the other card's button too. Its own key now (DS-47).
        label={t("mobile.home.glucoseSave", { defaultValue: "Save" })}
        accessibilityLabel={t("mobile.home.glucoseSaveA11y", {
          defaultValue: "Save glucose reading",
        })}
        onPress={() => void submit()}
        loading={saving}
        disabled={numOrUndef(value) === undefined}
        style={styles.saveBtn}
      />
    </View>
  );
}

